import { useCallback, useRef } from 'react';
import { useUploadMediaAsset, type FileType } from '@/hooks/useUploadMediaAsset';
import { validateFile } from '@/utils/nodeInputValidation';
import { RunType } from '@/enums/run-type.enum';
import type { MediaAsset } from '@/types/api/assets';
import type { MediaIteratorInput } from '@/types/design-app.types';

const MAX_FILES = 50;

export function useMediaIterator({
  id,
  value,
  onChange,
  acceptedFileType,
  disabled,
  isLoading,
  setIsUploading,
}: {
  id: string;
  value: MediaIteratorInput['value'];
  onChange: (id: string, value: MediaIteratorInput['value']) => void;
  acceptedFileType: FileType;
  disabled: boolean;
  isLoading: boolean;
  setIsUploading: (isUploading: boolean) => void;
}) {
  const files = value?.files ?? [];

  // Ref keeps the latest files so upload callbacks that fire after a stale
  // closure was captured (e.g. multi-file drop) still read the current list.
  const filesRef = useRef(files);
  filesRef.current = files;

  const handleUpload = useCallback(
    (asset: MediaAsset) => {
      const fileKind = validateFile(asset);
      if (!fileKind) return;
      filesRef.current = [...filesRef.current, fileKind];
      onChange(id, { files: filesRef.current });
    },
    [id, onChange],
  );

  const handleRemove = useCallback(
    (index: number) => {
      filesRef.current = filesRef.current.filter((_, i) => i !== index);
      onChange(id, { files: filesRef.current });
    },
    [id, onChange],
  );

  const uploadState = useUploadMediaAsset({
    runType: RunType.WorkflowExecution,
    disabled: disabled || isLoading,
    onUpload: handleUpload,
    allowMultiple: true,
    setIsUploading,
    acceptedFileType,
  });

  const { dropzoneState, isInProgress } = uploadState;
  const { getRootProps, getInputProps, open } = dropzoneState;
  const canAdd = !disabled && files.length < MAX_FILES;

  return {
    files,
    maxFiles: MAX_FILES,
    canAdd,
    handleUpload,
    handleRemove,
    getRootProps,
    getInputProps,
    open,
    isInProgress,
    uploadState,
  };
}
